/* eslint-disable camelcase */
import * as React from 'react';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { FetchResult } from 'react-apollo';

import type { ApolloError, ApolloQueryResult } from 'apollo-client';
import { isNil } from 'lodash';

import Retracked from 'js/app/retracked';
import user from 'js/lib/user';

import {
  getAndSaveIndividualAnonLearnerData,
  getMemoizedRandomLearnerDataFn,
} from 'bundles/account-profile/common/anonymizationUtils';
import { useEnterpriseProperties } from 'bundles/account-profile/common/eventing/useEnterpriseProperties';
import type { LearnerProfile } from 'bundles/account-profile/common/types';
import type { SharedEventingV3Data } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/types';
import type { LearnerProfileByExternalIdQuery } from 'bundles/account-profile/components/recruiting-profile-viewer/queries/__generated__/LearnerProfileByExternalId';
import type { LearnerProfileMeQuery } from 'bundles/account-profile/components/recruiting-profile-viewer/queries/__generated__/LearnerProfileMe';
import type { SharedLearnerProfile_PortfolioProjectsFragment as LearnerProfilePortfolioProject } from 'bundles/account-profile/components/recruiting-profile-viewer/queries/__generated__/SharedLearnerProfile_portfolioProjects';
import { useLearnerProfilePatchPersonalInformation } from 'bundles/account-profile/components/recruiting-profile-viewer/queries/hooks/useLearnerProfilePatchPersonalInformation';
import type { UpdateLearnerProfileMutationResponse } from 'bundles/account-profile/components/recruiting-profile-viewer/queries/hooks/useUpdateLearnerProfile';
import { useUpdateLearnerProfile } from 'bundles/account-profile/components/recruiting-profile-viewer/queries/hooks/useUpdateLearnerProfile';
import { useRestrictHiringSolutions } from 'bundles/employability-common/feature-flags/employabilityProductFeature';
import { shouldEnableGlobalCHSOptInFlow } from 'bundles/employability-common/feature-flags/learnerProfileEpicFeatureFlags';
import { useEnableLearnerProfileRecruiting } from 'bundles/employability-common/productFeatureFlags';
import type { SharedPortfolioProject_UserPortfolioProjectFragment as UserPortfolioProject } from 'bundles/portfolio-projects/queries/__generated__/SharedPortfolioProject_UserPortfolioProject';
import type { Nullable } from 'bundles/portfolio-projects/types';
import { mapUserProjectToLearnerProfileProject } from 'bundles/portfolio-projects/utils/typeMappers';

import _t from 'i18n!nls/account-profile';

type PartialProfileState = Partial<LearnerProfile>;

type RecruitingOrganization = {
  recruiting_organization_id?: string;
};

type Namespace = {
  page: string;
};

type LearnerProfileEventingData = {
  user_id?: number;
  candidate_profile?: string;
  program?: RecruitingOrganization;
  namespace?: Namespace;
};

export type ProfileContextType = {
  isProfileLoading: boolean;
  isRecruiter: boolean;
  editable: boolean;
  profileState?: PartialProfileState;
  retrackedEventingData: LearnerProfileEventingData;
  sharedEventingV3Data: SharedEventingV3Data;
  error?: ApolloError;
  /**
   * In the case of a courseraHistoryError we want to handle this differently from other errors
   * A Coursera History error is non blocking to page although not ideal so we would like to conitnue
   * to render the page whilst showing errors for the sections that are not loading
   */
  courseraHistoryFailedToLoad: boolean;
  /**
   * Since we are using errorPolicy: all to return partial data with errors, if the learnerProfile is null
   * this will be set to true
   */
  learnerProfileFailedToLoad: boolean;
  enableGlobalCHSOptInFlow?: boolean;
  refetchLearnerProfileMe?: () => Promise<ApolloQueryResult<LearnerProfileMeQuery>> | Promise<undefined>;
  refetchLearnerProfileExternalId?: () =>
    | Promise<ApolloQueryResult<LearnerProfileByExternalIdQuery>>
    | Promise<undefined>;
  updateLearnerProfile: (
    updatedData: PartialProfileState,
    skipNetwork?: boolean
  ) => Promise<void | FetchResult<UpdateLearnerProfileMutationResponse>>;
  updatePortfolioProject: (project: UserPortfolioProject) => void;
  restrictHiringSolutions: boolean;
};

const Wrapper: React.LegacyFunctionComponentWithChildren = ({ children }) => {
  return <>{children}</>;
};

const sortProjectsByCompletedDate = (
  projectA: LearnerProfilePortfolioProject,
  projectB: LearnerProfilePortfolioProject
) => {
  // @ts-expect-error DateTime: string
  const completedAtA = new Date(projectA.completed).getTime();
  // @ts-expect-error DateTime: string
  const completedAtB = new Date(projectB.completed).getTime();
  // Sorting the Portfolio Projects by most recent completed date
  return completedAtB - completedAtA;
};

const TrackedWrapper = Retracked.createTrackedContainer<
  React.PropsWithChildren<{ retrackedEventingData?: LearnerProfileEventingData }>
>((props) => ({
  ...props.retrackedEventingData,
}))(Wrapper);

export const ProfileContext = createContext<ProfileContextType>({
  isProfileLoading: true,
  isRecruiter: false,
  restrictHiringSolutions: false,
  editable: false,
  profileState: {},
  retrackedEventingData: {},
  sharedEventingV3Data: {
    candidate: {},
    enterprise: {},
  },
  error: undefined,
  courseraHistoryFailedToLoad: false,
  learnerProfileFailedToLoad: false,
  enableGlobalCHSOptInFlow: false,
  refetchLearnerProfileMe: () => Promise.resolve(undefined),
  refetchLearnerProfileExternalId: () => Promise.resolve(undefined),
  updateLearnerProfile: () => new Promise(() => null),
  updatePortfolioProject: () => undefined,
});

type PropsFromCaller = {
  children: React.ReactNode;
  isProfileLoading: boolean;
  learnerProfile?: LearnerProfile | null;
  refetchLearnerProfileMe?: () => Promise<ApolloQueryResult<LearnerProfileMeQuery>> | Promise<undefined>;
  refetchLearnerProfileExternalId?: () =>
    | Promise<ApolloQueryResult<LearnerProfileByExternalIdQuery>>
    | Promise<undefined>;
  error?: ApolloError;
  isEditMode: boolean;
};

/**
 * We want to handle Coursera History errors differently than other errors because we don't want
 * to show the not found page as the data is non critical to the page
 *  */
const isACourseraHistoryError = (error?: ApolloError) => {
  if (!error) {
    return false;
  }
  const internalErrors = error?.graphQLErrors.filter(
    (graphqlError) => graphqlError.extensions?.errorType === 'INTERNAL'
  );
  const courseraHistoryError = internalErrors?.find((graphqlError) => {
    const pathLength = graphqlError.path?.length || 0;
    return graphqlError.path?.[pathLength - 1] === 'courseraHistory';
  });
  return !!courseraHistoryError;
};

export const ProfileContextProvider = ({
  children,
  learnerProfile,
  isEditMode,
  isProfileLoading,
  refetchLearnerProfileMe,
  refetchLearnerProfileExternalId,
  error,
}: PropsFromCaller) => {
  const [courseraHistoryFailedToLoadOnRefetch, setCourseraHistoryFailedToLoadOnRefetch] = useState<boolean>(false);
  const recruiterOrg = learnerProfile?.recruitingOrganizationOfViewer;
  const { recruitingAnonymizationEnabled } = useEnableLearnerProfileRecruiting(recruiterOrg?.id ?? '');
  const enterpriseProperties = useEnterpriseProperties();
  const restrictHiringSolutions = useRestrictHiringSolutions().restrictHiringSolutions;

  const { fullName: defaultAnonFullName, photoUrl: defaultAnonPhotoUrl } = useMemo(() => {
    // Since localStorage is only available on the client, show a default mock name and photo for the server
    if (typeof window === 'undefined') {
      return {
        fullName: _t('Full Name'),
        photoUrl: '',
      };
    }

    const getRandomEntry = getMemoizedRandomLearnerDataFn();
    return getRandomEntry();
  }, []);

  const getProfileData = useCallback<() => $TSFixMe>(() => {
    if (learnerProfile) {
      const updatedLearnerProfile = {
        ...learnerProfile,
        completedPortfolioProjects: [...(learnerProfile.completedPortfolioProjects || [])].sort(
          sortProjectsByCompletedDate
        ),
        draftPortfolioProjects: [...(learnerProfile.draftPortfolioProjects || [])].sort(sortProjectsByCompletedDate),
      };

      return recruitingAnonymizationEnabled && !isEditMode
        ? getAndSaveIndividualAnonLearnerData(updatedLearnerProfile, defaultAnonFullName, defaultAnonPhotoUrl)
        : updatedLearnerProfile;
    }

    return undefined;
  }, [defaultAnonFullName, defaultAnonPhotoUrl, isEditMode, learnerProfile, recruitingAnonymizationEnabled]);

  const [profileState, setProfileState] = useState<PartialProfileState | undefined>(getProfileData());
  const editable = isEditMode;

  const isRecruiter = !!profileState?.recruitingOrganizationOfViewer?.id && !isEditMode;

  // useEffect only runs after mount/update, which means if we only set the profile state in useEffect it will only be set once the DOM is loaded
  // and we'll get a page flash of empty data
  useEffect(() => {
    setProfileState(getProfileData());
  }, [getProfileData, profileState?.externalId, recruitingAnonymizationEnabled]);

  const retrackedEventingData: LearnerProfileEventingData = {
    user_id: user?.get()?.id,
    candidate_profile: profileState?.externalId,
    program: {
      recruiting_organization_id: profileState?.recruitingOrganizationOfViewer?.id,
    },
    namespace: {
      page: 'learner_profile',
    },
  };

  const { updateLearnerProfileMutation } = useUpdateLearnerProfile();
  const { updateLearnerProfilePatchPersonalInformation } = useLearnerProfilePatchPersonalInformation();

  const updateLearnerProfilePatchPersonalInfo = async (updatedData: PartialProfileState) => {
    try {
      const res = await updateLearnerProfilePatchPersonalInformation({
        ...(!isNil(updatedData.countryWorkEligibilities?.US?.hasWorkAuthorization) &&
        !isNil(updatedData.countryWorkEligibilities?.US?.requiresVisaSponsorship)
          ? {
              countryWorkEligibilities: {
                countryWorkEligibilities: {
                  US: {
                    hasWorkAuthorization: updatedData.countryWorkEligibilities?.US?.hasWorkAuthorization,
                    requiresVisaSponsorship: updatedData.countryWorkEligibilities?.US?.requiresVisaSponsorship,
                  },
                },
              },
            }
          : {
              countryWorkEligibilities: {
                remove: {},
              },
            }),
        ...('ethnicities' in updatedData &&
          updatedData?.ethnicities && {
            ethnicities: {
              ethnicities: updatedData?.ethnicities,
            },
          }),
        veteranStatus: {
          ...('veteranStatus' in updatedData && updatedData?.veteranStatus
            ? {
                veteranStatus: updatedData?.veteranStatus,
              }
            : {
                remove: {},
              }),
        },
      });

      const learnerProfilePatchInformationData = res.data?.LearnerProfile_PatchPersonalInformation;

      if (learnerProfilePatchInformationData?.__typename === 'LearnerProfile_PatchFailure') {
        let errorList = '';

        learnerProfilePatchInformationData.errors.forEach((err) => {
          errorList += err.message;
        });
        throw new Error(errorList);
      } else if (learnerProfilePatchInformationData?.__typename === 'LearnerProfile_PatchSuccess') {
        const updatedProfileData = learnerProfilePatchInformationData.profile;
        setProfileState({
          ...profileState,
          countryWorkEligibilities: updatedProfileData?.countryWorkEligibilities,
          veteranStatus: updatedProfileData?.veteranStatus,
          ethnicities: updatedProfileData?.ethnicities,
        });
      }
    } catch (e) {
      throw e;
    }
  };

  const updateLearnerProfile = async (updatedData: PartialProfileState, skipNetwork?: boolean) => {
    if (!profileState || !editable || !user.get()?.id) return;

    if (skipNetwork) {
      setProfileState((prevState) => ({
        ...prevState,
        ...updatedData,
      }));
      return;
    }

    let updatedCountryCode;
    let profileStateCountryCode;

    if (updatedData.location && 'countryCode' in updatedData.location) {
      updatedCountryCode = updatedData.location?.countryCode;
    }

    if (profileState?.location && 'countryCode' in profileState.location) {
      profileStateCountryCode = profileState.location.countryCode;
    }

    const hasLocationChanged = isNil(updatedCountryCode) ? false : updatedCountryCode !== profileStateCountryCode;

    const hasResumeChanged = isNil(updatedData.resumeUrl) ? false : updatedData.resumeUrl !== profileState?.resumeUrl;

    try {
      const res = await updateLearnerProfileMutation({ ...profileState, ...updatedData });

      await updateLearnerProfilePatchPersonalInfo({ ...profileState, ...updatedData });

      if (res?.data) {
        if (res.data.error) {
          throw new Error(res.data.error.message);
        } else {
          const updatedProfileState = {
            ...profileState,
            ...updatedData,
          };
          setProfileState({
            ...updatedProfileState,
          });
          if ((hasLocationChanged || hasResumeChanged) && refetchLearnerProfileMe) {
            try {
              const refetchLearnerProfileResult = await refetchLearnerProfileMe();
              if (refetchLearnerProfileResult?.data) {
                if (refetchLearnerProfileResult.error) {
                  if (isACourseraHistoryError(refetchLearnerProfileResult.error)) {
                    setCourseraHistoryFailedToLoadOnRefetch(true);
                  } else {
                    throw new Error(refetchLearnerProfileResult.error.message);
                  }
                } else {
                  setCourseraHistoryFailedToLoadOnRefetch(false);
                  setProfileState((prevState) => ({
                    ...prevState,
                    ...refetchLearnerProfileResult.data.LearnerProfile?.me,
                  }));
                }
              }
            } catch (e) {
              throw e;
            }
          }
        }
      }
    } catch (e) {
      throw e;
    }
  };

  const getUpdatedState = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (project: UserPortfolioProject): Nullable<PartialProfileState> => {
      if (!profileState) {
        return null;
      }
      const { draftPortfolioProjects, completedPortfolioProjects } = profileState;
      const updatedProject = mapUserProjectToLearnerProfileProject(project);

      let updatedDraftPortfolioProject = [...(draftPortfolioProjects || [])];
      let updatedCompletedPortfolioProjects = [...(completedPortfolioProjects || [])];

      if (draftPortfolioProjects) {
        const indexInDraftProject = draftPortfolioProjects.findIndex((p) => p.id === project.project.id);
        if (indexInDraftProject >= 0) {
          if (project.isPublished) {
            updatedDraftPortfolioProject = draftPortfolioProjects.filter((p) => p.id !== project.project.id);
            updatedCompletedPortfolioProjects = [...updatedCompletedPortfolioProjects, updatedProject].sort(
              sortProjectsByCompletedDate
            );
          } else {
            updatedDraftPortfolioProject[indexInDraftProject] = updatedProject;
          }
        }
      }
      if (completedPortfolioProjects) {
        const indexInCompletedProjects = completedPortfolioProjects.findIndex((p) => p.id === project.project.id);
        if (indexInCompletedProjects >= 0) {
          if (project.isPublished) {
            updatedCompletedPortfolioProjects[indexInCompletedProjects] = updatedProject;
          } else {
            updatedDraftPortfolioProject = [...updatedDraftPortfolioProject, updatedProject].sort(
              sortProjectsByCompletedDate
            );
            updatedCompletedPortfolioProjects = completedPortfolioProjects.filter((p) => p.id !== project.project.id);
          }
        }
      }

      return {
        ...profileState,
        ...{
          draftPortfolioProjects: updatedDraftPortfolioProject,
          completedPortfolioProjects: updatedCompletedPortfolioProjects,
        },
      };
    },
    [profileState]
  );

  const updatePortfolioProject = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (project: UserPortfolioProject) => {
      const updatedState = getUpdatedState(project);
      if (updatedState) {
        setProfileState(updatedState);
      }
    },
    [getUpdatedState]
  );

  const enableGlobalCHSOptInFlow = shouldEnableGlobalCHSOptInFlow();

  const sharedEventingV3Data: SharedEventingV3Data = {
    candidate: {
      externalId: profileState?.externalId,
    },
    enterprise: {
      organizationSlug: enterpriseProperties?.organizationSlug,
      programSlug: enterpriseProperties?.programSlug,
    },
  };

  return (
    <ProfileContext.Provider
      value={{
        profileState,
        isRecruiter,
        editable,
        isProfileLoading,
        retrackedEventingData,
        sharedEventingV3Data,
        error,
        // The data being null on load means the data failed to load. This a temporary fix until we can
        // fix Apollo errors being swallowed in SSR
        courseraHistoryFailedToLoad: learnerProfile?.courseraHistory === null || courseraHistoryFailedToLoadOnRefetch,
        learnerProfileFailedToLoad: learnerProfile === null,
        updateLearnerProfile,
        refetchLearnerProfileMe,
        refetchLearnerProfileExternalId,
        updatePortfolioProject,
        enableGlobalCHSOptInFlow,
        restrictHiringSolutions,
      }}
    >
      <TrackedWrapper retrackedEventingData={retrackedEventingData}>{children}</TrackedWrapper>
    </ProfileContext.Provider>
  );
};

export default ProfileContextProvider;
